import { DocumentData, QuerySnapshot, Unsubscribe, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { DeviationsLoader } from "../deviations.loader";
import { DeviationsLoaderUtils } from "../deviations.loader.utils";
import { chunk } from "lodash";
import { LocalData } from "to-be-removed/LocalData/General/LocalData";
import { Role } from "to-be-removed/LocalData/User/Role";
import { IRawDeviation } from "@propely/propely-typings";

export class DeviationsLastUpdateLoader {
  deviationsLoader: DeviationsLoader;

  private static limitSize = 10;
  private subscriptions: Unsubscribe[] = [];
  lastUpdateLoaded = () => this.subscriptions.length > 0;

  constructor(deviationsLoader: DeviationsLoader) {
    this.deviationsLoader = deviationsLoader;
  }

  private snapshotsToLoad = 0;
  private snapshotsLoaded = 0;
  private addedQueue: IRawDeviation[] = [];
  private removedQueue: IRawDeviation[] = [];

  private handleSnapshot = (snapshot: QuerySnapshot<DocumentData>) => {
    const addedOrModifiedChanges = snapshot.docChanges().filter((change) => change.type === "added" || change.type === "modified");
    const removedChanges = snapshot.docChanges().filter((change) => change.type === "removed");

    const addedOrModifiedDeviations = addedOrModifiedChanges
      .map((change) => DeviationsLoaderUtils.parseIRawDeviationFromDocumentSnapshot(change.doc))
      .filter((deviation) => deviation !== undefined) as IRawDeviation[];
    const removedDeviations = removedChanges
      .map((change) => DeviationsLoaderUtils.parseIRawDeviationFromDocumentSnapshot(change.doc))
      .filter((deviation) => deviation !== undefined) as IRawDeviation[];

    this.addedQueue.push(...addedOrModifiedDeviations);
    this.removedQueue.push(...removedDeviations);
    this.checkIfAllLoaded();
  };

  private checkIfAllLoaded() {
    this.snapshotsLoaded++;
    if (this.snapshotsLoaded >= this.snapshotsToLoad) {
      this.deviationsLoader.addDeviations(this.addedQueue, "DeviationsLastUpdateLoader-added");
      this.deviationsLoader.removeDeviations(this.removedQueue, "DeviationsLastUpdateLoader-removed");
      this.addedQueue = [];
      this.removedQueue = [];
    }
  }

  setupLastUpdateListener() {
    this.snapshotsToLoad = 0;
    if (this.subscriptions.length > 0) {
      for (const subscription of this.subscriptions) {
        subscription();
      }
    }

    console.log("[DeviationsLastUpdateLoader] Loading deviations");

    const maxLastUpdateDate = new Date();
    maxLastUpdateDate.setHours(maxLastUpdateDate.getHours() + 1);

    /**
     * If the user is an operation manager or landlord, they can read all deviations from their properties.
     */
    const landlordOrOperationManagerChunks = DeviationsLoaderUtils.getLandlordOrOperationManagerPropertyChunks();
    for (const chunk of landlordOrOperationManagerChunks) {
      this.snapshotsToLoad++;
      const listener = onSnapshot(
        query(
          DeviationsLoaderUtils.getCollectionGroup(),
          DeviationsLoaderUtils.getPropertyIdWhere(chunk),
          orderBy("lastUpdate", "desc"),
          where("lastUpdate", "<", maxLastUpdateDate),
          limit(DeviationsLastUpdateLoader.limitSize)
        ),
        this.handleSnapshot,
        (error) => {
          console.error("[DeviationsLastUpdateLoader] Error loading deviations");
          console.error(error);
        }
      );
      this.subscriptions.push(listener);
    }

    /**
     * Contractor members can read all deviations for their contractor groups.
     */
    const contractorsArrays = chunk(
      LocalData.currentUser.contractorGroups.map((group) => group.id),
      30
    );
    for (const contractors of contractorsArrays) {
      if (contractors.length === 0) continue;
      this.snapshotsToLoad++;
      const listener = onSnapshot(
        query(
          DeviationsLoaderUtils.getCollectionGroup(),
          DeviationsLoaderUtils.getContractorGroupsWhere(contractors),
          orderBy("lastUpdate", "desc"),
          where("lastUpdate", "<", maxLastUpdateDate),
          limit(DeviationsLastUpdateLoader.limitSize)
        ),
        this.handleSnapshot,
        (error) => {
          console.error("[DeviationsLastUpdateLoader] Error loading deviations");
          console.error(error);
        }
      );
      this.subscriptions.push(listener);
    }

    /**
     * Tenant members read per property
     */
    let properties = LocalData.currentUser.properties.filter((property) => !property.deleted);

    for (const property of properties) {
      const member = property.members.getData(LocalData.currentUser.id);
      if (member?.roles.includes(Role.RENTER)) {
        this.snapshotsToLoad++;
        const allTenantsListener = onSnapshot(
          query(
            DeviationsLoaderUtils.getCollection(property.id),
            where("visibility.allTenants", "==", true),
            orderBy("lastUpdate", "desc"),
            where("lastUpdate", "<", maxLastUpdateDate),
            limit(DeviationsLastUpdateLoader.limitSize)
          ),
          this.handleSnapshot,
          (error) => {
            console.error("[DeviationsLastUpdateLoader] Error loading deviations");
            console.error(error);
          }
        );
        this.subscriptions.push(allTenantsListener);

        const unitChunks = chunk([...member.units.map((unit) => unit.id)], 10);
        for (const chunk of unitChunks) {
          if (chunk.length === 0) continue;
          this.snapshotsToLoad++;
          const tenantListener = onSnapshot(
            query(
              DeviationsLoaderUtils.getCollection(property.id),
              where("visibility.tenants", "==", true),
              where("unitIDs", "array-contains-any", chunk),
              orderBy("lastUpdate", "desc"),
              where("lastUpdate", "<", maxLastUpdateDate),
              limit(DeviationsLastUpdateLoader.limitSize)
            ),
            this.handleSnapshot,
            (error) => {
              console.error("[DeviationsLastUpdateLoader] Error loading deviations");
              console.error(error);
            }
          );
          this.subscriptions.push(tenantListener);
        }
      }
    }
  }

  removeLastUpdateListener() {
    for (const subscription of this.subscriptions) {
      subscription();
    }
    this.subscriptions = [];
  }
}

